import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, Play, X, ChevronRight, Hammer } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const PAGES_PER_FETCH = 3;

const Stat = ({ label, value, mono = false, className = "" }) => (
  <div className="rounded-md border border-white/5 bg-[#0c0f14] p-2.5">
    <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`mt-0.5 text-sm font-semibold text-white ${mono ? "font-mono" : ""} ${className}`}>{value}</div>
  </div>
);

const FurniGrid = ({ items, onPick }) => (
  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
    {items.map((it) => (
      <button
        key={it.classname}
        data-testid={`furniture-item-${it.classname}`}
        onClick={() => onPick(it)}
        className="card-hover group flex flex-col items-center gap-1 rounded-xl border border-white/10 bg-[#10141b] p-3 hover:border-[#7c4dff]/50"
        title={`${it.name} – ${it.classname}`}
      >
        <div className="flex h-16 w-16 items-center justify-center">
          <img src={it.icon} alt={it.classname} className="hb-pixel-img max-h-16 max-w-16" loading="lazy" />
        </div>
        <div className="line-clamp-2 text-center text-[11px] leading-tight text-zinc-200">{it.name}</div>
        <div className="truncate text-[9px] font-mono uppercase tracking-wider text-[#9d7bff]">{it.classname}</div>
      </button>
    ))}
  </div>
);

const SwfModal = ({ item, onClose }) => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);

  // Detail / SWF-Header laden (parallel zu Ruffle)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const key = item.id || item.classname;
        const d = await api.furnitureDetail(key);
        if (!cancelled) setDetail(d);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item.id, item.classname]);

  useEffect(() => {
    let cancelled = false;
    // Globaler Schutz: Ruffle wirft asynchron aus WASM heraus ("Incorrect locale information")
    // window.onerror Script-Errors aus dem Ruffle-Bundle abfangen, damit React-DevOverlay nicht zuschlaegt.
    const isRuffleError = (msg, source) => {
      const s = (source || "").toString();
      const m = (msg || "").toString();
      return s.includes("ruffle") || s.includes("@ruffle") || m.includes("locale") || m.includes("Script error");
    };
    const onErr = (msg, source) => {
      if (isRuffleError(msg, source)) {
        if (!cancelled) { setErr(typeof msg === "string" ? msg : "Ruffle-Fehler"); setStatus("error"); }
        return true; // swallow
      }
      return false;
    };
    const onUnhandled = (e) => {
      const msg = (e?.reason?.message || e?.reason || "").toString();
      const stack = (e?.reason?.stack || "").toString();
      if (isRuffleError(msg, stack)) {
        e.preventDefault();
        if (!cancelled) { setErr(msg || "Ruffle-Fehler"); setStatus("error"); }
      }
    };
    const prevOnError = window.onerror;
    window.onerror = (msg, source, lineno, colno, error) => {
      if (onErr(msg, source)) return true;
      return typeof prevOnError === "function" ? prevOnError(msg, source, lineno, colno, error) : false;
    };
    window.addEventListener("unhandledrejection", onUnhandled);

    (async () => {
      try {
        // Warte auf Ruffle CDN bis maximal ~3s
        for (let i = 0; i < 6 && !window.RufflePlayer; i++) await new Promise((r) => setTimeout(r, 500));
        if (!window.RufflePlayer || !window.RufflePlayer.newest) {
          throw new Error("Ruffle ist nicht geladen.");
        }
        if (cancelled || !containerRef.current) return;
        // Web Component verwenden – das fängt interne Ruffle-Fehler in der Shadow-Root
        const el = document.createElement("ruffle-player");
        el.setAttribute("data-testid", "ruffle-player");
        el.style.width = "100%";
        el.style.height = "420px";
        el.style.background = "#0a0d12";
        el.addEventListener("loadeddata", () => { if (!cancelled) setStatus("ready"); });
        containerRef.current.appendChild(el);
        // load asynchron – wrap separat
        const url = api.furnitureSwfUrl(item.classname);
        try {
          if (typeof el.load === "function") {
            await el.load({ url, autoplay: "on", unmuteOverlay: "hidden", logLevel: "error" });
          } else {
            el.src = url;
          }
          if (!cancelled) setStatus("ready");
        } catch (e) {
          if (!cancelled) { setErr(e?.message || String(e)); setStatus("error"); }
        }
      } catch (e) {
        if (!cancelled) { setErr(e?.message || String(e)); setStatus("error"); }
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener("unhandledrejection", onUnhandled);
      window.onerror = prevOnError;
      try { if (containerRef.current) containerRef.current.innerHTML = ""; } catch { /* ignore */ }
    };
  }, [item.classname]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="swf-modal"
    >
      <div
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0f141b] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-white/10 p-4">
          <img src={item.icon} alt={item.classname} className="hb-pixel-img h-12 w-12" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-white">{item.name}</div>
            <div className="truncate text-xs font-mono text-[#9d7bff]">{item.classname}</div>
          </div>
          <a
            href={item.swf}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost rounded-lg px-3 py-1.5 text-xs"
          >.swf öffnen</a>
          <button
            onClick={onClose}
            data-testid="swf-modal-close"
            className="rounded-lg p-2 text-zinc-300 hover:bg-white/5"
          ><X className="h-4 w-4" /></button>
        </div>

        <div className="bg-[#0a0d12] p-4">
          <div ref={containerRef} data-testid="ruffle-container" className="mx-auto w-full overflow-hidden rounded-lg border border-white/5 bg-[#0a0d12]" style={{ minHeight: 420 }}>
            {status === "loading" && (
              <div className="flex h-[420px] items-center justify-center text-zinc-400">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> SWF wird geladen …
              </div>
            )}
            {status === "error" && (
              <div className="flex h-[420px] flex-col items-center justify-center gap-2 text-rose-300">
                <div className="text-sm font-semibold">SWF konnte nicht abgespielt werden</div>
                <div className="text-xs text-zinc-400">{err}</div>
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] text-zinc-500">
            SWF live abgespielt via <span className="font-mono">Ruffle.rs</span>, Daten gestreamt von habboassets.com.
          </p>
        </div>

        {/* Detail-Metadaten */}
        <div className="border-t border-white/10 bg-[#0c0f14] p-4" data-testid="furniture-detail">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#9d7bff]">Möbel-Metadaten</div>
            {detail?.id && <div className="text-[10px] text-zinc-500">HabboAssets ID #{detail.id}</div>}
          </div>
          {detailLoading ? (
            <div className="flex items-center justify-center py-6 text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : !detail ? (
            <div className="text-xs italic text-zinc-500">Keine Metadaten verfügbar.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Typ" value={detail.type === "s" ? "Boden" : detail.type === "i" ? "Item" : detail.type === "w" ? "Wand" : detail.type || "–"} />
              <Stat label="Größe" value={detail.width && detail.length ? `${detail.width}×${detail.length}` : "–"} />
              <Stat label="Stapelhöhe" value={detail.stack_height ?? "–"} />
              <Stat label="Sprite-ID" value={detail.sprite_id ?? "–"} mono />
              <Stat label="Mall-Preis" value={<span className="italic text-zinc-500" title="Nicht in HabboAssets-Daten enthalten">n/a</span>} />
              <Stat label="Stapelbar" value={detail.can_stack ? "Ja" : "Nein"} />
              <Stat label="Sitzbar" value={detail.can_sit ? "Ja" : "Nein"} />
              <Stat label="Rare" value={detail.is_rare ? "★ Ja" : "Nein"} className={detail.is_rare ? "text-amber-300" : ""} />
            </div>
          )}

          {/* SWF-Header */}
          {detail?.swf_header && (
            <div className="mt-4 rounded-lg border border-white/5 bg-[#10141b] p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#ffb84d]">SWF-Header</div>
                {detail.swf_bytes != null && (
                  <div className="text-[10px] text-zinc-500">{(detail.swf_bytes / 1024).toFixed(1)} kB komprimiert</div>
                )}
              </div>
              {detail.swf_header.error ? (
                <div className="text-xs text-rose-300">{detail.swf_header.error}</div>
              ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Stat label="Kompression" value={detail.swf_header.compression || "–"} mono />
                  <Stat label="SWF-Version" value={detail.swf_header.version ?? "–"} mono />
                  <Stat label="Frame-Rate" value={detail.swf_header.framerate != null ? `${detail.swf_header.framerate} fps` : "–"} />
                  <Stat label="Frame-Count (Lines)" value={detail.swf_header.framecount ?? "–"} mono />
                  <Stat label="Dimensionen" value={detail.swf_header.width && detail.swf_header.height ? `${detail.swf_header.width}×${detail.swf_header.height}` : "–"} />
                  <Stat label="Shape-Count" value={detail.swf_header.shape_count ?? "–"} mono />
                  <Stat label="Tags total" value={detail.swf_header.tags_total ?? "–"} mono />
                  <Stat label="Filesize (unzipped)" value={detail.swf_header.filesize != null ? `${(detail.swf_header.filesize / 1024).toFixed(1)} kB` : "–"} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Furniture = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [active, setActive] = useState(null);

  const load = async (currentPage = 1, query = "") => {
    setLoading(true);
    try {
      const data = await api.listFurniture(query, currentPage, PAGES_PER_FETCH);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1, ""); }, []);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    load(1, q.trim());
  };

  const onLoadMore = () => {
    const next = page + PAGES_PER_FETCH;
    setPage(next);
    (async () => {
      setLoading(true);
      try {
        const data = await api.listFurniture(q.trim(), next, PAGES_PER_FETCH);
        const existing = new Set(items.map((i) => i.classname));
        const merged = [...items, ...((data.items || []).filter((i) => !existing.has(i.classname)))];
        setItems(merged);
      } finally { setLoading(false); }
    })();
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-10" data-testid="furniture-page">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">Möbel</div>
      <h1 className="mt-1 flex items-center gap-2 text-3xl font-bold text-white"><Hammer className="h-7 w-7 text-[#ffb84d]" /> Habbo-Möbel-Archiv</h1>
      <p className="mt-2 max-w-2xl text-sm text-zinc-400">
        Stöbere durch tausende Habbo-Möbel live von habboassets.com. Klicke ein Möbel an, um die Original-<span className="font-mono">.swf</span>-Datei direkt im Browser auszuprobieren – emuliert via Ruffle.
      </p>

      <form onSubmit={onSearch} className="mt-6 flex max-w-xl items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="furniture-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Klassennamen oder Möbelname (z.B. throne, seaside)..."
            className="pl-9 bg-[#10141b] border-white/10 text-zinc-200"
          />
        </div>
        <Button type="submit" data-testid="furniture-search-btn" className="btn-primary rounded-lg px-4 text-sm">
          Suchen
        </Button>
      </form>

      <div className="mt-8">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-white/5 bg-[#10141b] p-12 text-center text-sm text-zinc-500">
            Keine Möbel gefunden – Suche anpassen.
          </div>
        ) : (
          <>
            <div className="mb-3 text-xs text-zinc-500">{items.length} Möbel geladen</div>
            <FurniGrid items={items} onPick={setActive} />
            <div className="mt-6 flex justify-center">
              <Button
                data-testid="furniture-load-more"
                onClick={onLoadMore}
                disabled={loading}
                className="btn-ghost rounded-lg px-5 py-2 text-sm"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ChevronRight className="mr-2 h-4 w-4" />}
                Weitere Seiten laden
              </Button>
            </div>
          </>
        )}
      </div>

      {active && <SwfModal item={active} onClose={() => setActive(null)} />}
    </section>
  );
};

export default Furniture;
